import React from 'react';
import JobCategoryPage from './JobCategoryPage';
import { GraduationCap } from 'lucide-react';

const SchoolSocialWorkerJobs = () => {
  const contentSections = [
    {
      title: "What is a School Social Worker?",
      content: (
        <p>
          School social workers are licensed mental health professionals who work within educational settings to support student success, well-being, and academic achievement. These specialists serve as a vital link between students, families, educators, and community resources. School social workers address barriers to learning, provide counseling services, and advocate for students facing social, emotional, behavioral, and environmental challenges. They play a critical role in creating safe, supportive school environments where all students can thrive.
        </p>
      )
    },
    {
      title: "School Social Worker Job Responsibilities",
      content: (
        <p>
          School social workers wear many hats, providing direct services to students while also working systemically to improve school climate and student outcomes. Their responsibilities span individual counseling, group work, family engagement, and community partnerships.
        </p>
      ),
      list: [
        "Provide individual and group counseling to students",
        "Conduct psychosocial assessments and develop intervention plans",
        "Collaborate with teachers, administrators, and support staff on student concerns",
        "Facilitate IEP (Individualized Education Program) meetings for students with disabilities",
        "Connect families with community resources and social services",
        "Implement crisis intervention and threat assessment protocols",
        "Develop and lead social-emotional learning programs",
        "Address attendance issues and truancy prevention",
        "Provide consultation to school staff on mental health and trauma-informed practices",
        "Advocate for students' rights and educational access"
      ]
    },
    {
      title: "Required Qualifications and Licenses",
      content: (
        <p>
          School social work positions require specific credentials that vary by state. Most positions require at minimum a Master of Social Work (MSW) degree, though some states allow LBSW practitioners in school settings with appropriate supervision. Many states also require a School Social Work credential or certification in addition to clinical social work licensure.
        </p>
      ),
      list: [
        "Master of Social Work (MSW) from a CSWE-accredited program",
        "State social work licensure (LCSW, LMSW, or state-specific school credential)",
        "School Social Work certification (Type 73 in Illinois, PPS in California, etc.)",
        "Experience working with children, adolescents, and families",
        "Knowledge of special education laws (IDEA, Section 504)",
        "Training in trauma-informed care and crisis intervention",
        "Background check clearance for working with minors"
      ]
    },
    {
      title: "Salary and Benefits for School Social Workers",
      content: (
        <p>
          School social workers typically enjoy competitive salaries along with the benefits of working on an academic calendar. Salaries range from $45,000 to $75,000+ depending on location, experience, and school district. Many school districts place social workers on the same salary schedule as teachers, with additional compensation for advanced degrees and certifications. Benefits often include summers off (though some positions are 12-month), excellent health insurance, pension or retirement plans, and job security through union representation in many districts.
        </p>
      )
    },
    {
      title: "Work Environment and Schedule",
      content: (
        <p>
          School social workers typically work within elementary, middle, or high school buildings, often serving multiple schools within a district. The academic calendar provides a unique work-life balance with summers, holidays, and school breaks off. Daily schedules align with school hours, though evening events and family meetings may occasionally extend the workday. The school setting offers a supportive, team-oriented environment with opportunities to build long-term relationships with students and see their growth over time.
        </p>
      )
    },
    {
      title: "Why Choose School Social Work?",
      content: (
        <p>
          School social work offers a unique opportunity to positively impact young people during their formative years. For those passionate about youth development, educational equity, and mental health, school social work combines clinical practice with advocacy in a rewarding setting. The ability to intervene early in a child's life, support families, and contribute to a positive school culture makes this specialty deeply fulfilling. School social workers often describe the joy of watching students overcome challenges and achieve success as the most rewarding aspect of their work.
        </p>
      )
    }
  ];

  const relatedCategories = [
    { name: "Clinical Social Worker Jobs", href: "/clinical-social-worker-jobs" },
    { name: "Hospital Social Worker Jobs", href: "/hospital-social-worker-jobs" },
    { name: "Mental Health Social Worker Jobs", href: "/mental-health-social-worker-jobs" },
    { name: "Community Social Worker Jobs", href: "/community-social-worker-jobs" },
  ];

  return (
    <JobCategoryPage
      category="school-social-worker-jobs"
      title="School Social Worker Jobs"
      metaTitle="School Social Worker Jobs | K-12 Education Positions | INeedASocialWorker"
      metaDescription="Find school social worker jobs in K-12 education nationwide. Browse positions in elementary, middle, and high schools. Support student success and well-being. Apply with SW Express Apply™."
      heroTitle="School Social Worker Jobs"
      heroSubtitle="Make a difference in students' lives with school social work positions at K-12 schools and districts across the United States."
      searchKeywords={["school social worker", "school social work", "K-12 social worker", "education social worker", "student services social worker", "school counselor social work"]}
      contentSections={contentSections}
      relatedCategories={relatedCategories}
      iconComponent={GraduationCap}
    />
  );
};

export default SchoolSocialWorkerJobs;
